/**
 * Pure helpers for the reforecast event-merge logic.
 *
 * These are extracted from the `reforecast` useCallback in App.jsx to eliminate
 * the duplicate inline definitions (main path + per-branch loop). All three
 * functions are stateless and produce byte-identical output to the original
 * inline code they replace.
 */
import { occurrenceKeyOf, stampOccurrenceKeys } from "./eventIdentity.js";

/**
 * Returns the effective end date of an event as a Date, or null.
 *
 * Uses `date_end` when present AND parseable (date-range events); otherwise falls
 * back to `date`. A present-but-unparseable `date_end` does NOT null out a validly-
 * dated event — it falls through to `date` (mirroring filterAndStampEvents' isPast
 * rule). Returns null only when neither `date_end` nor `date` yields a valid date.
 *
 * @param {object} e - A forecast event object.
 * @returns {Date|null}
 */
export const endDateOf = (e) => {
  // Effective end date: `date_end` when present AND parseable, otherwise fall back
  // to `date`. This mirrors filterAndStampEvents' isPast rule (lib/forecast/model.ts):
  // a present-but-unparseable `date_end` must NOT flip a validly-dated event to
  // "no end" — otherwise a past event with a garbage `date_end` gets misclassified
  // as future (dropped from the accumulated past / mis-restamped). Null only when
  // neither yields a valid date.
  const parse = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };
  return parse(e.date_end) || parse(e.date);
};

/**
 * Removes duplicate events from an array.
 *
 * An event is considered a duplicate of an earlier one when it shares:
 * - the same `id`, OR
 * - the same lowercased+trimmed title AND the same `date`.
 *
 * The first occurrence of each event is kept; subsequent duplicates are
 * dropped.  Events with no `id` AND no `title`+`date` pair pass through
 * unconditionally (no key → never matches).
 *
 * `opts.ignoreId` drops the `id` key, deduping on `title`+`date` (content) ONLY.
 * This is REQUIRED for the cross-vintage reforecast merge: the forecast server
 * assigns POSITIONAL event ids (`e1`, `e2`, … reset every run), so a given past
 * event from an old run and a genuinely DIFFERENT past event from a new run can
 * collide on id (`e3` vs `e3`). Keying on id would then drop the new one and the
 * accumulated past could never grow. Content (title+date) is the only stable
 * cross-run identity, so the merge dedups on that.
 *
 * @param {object[]} evts - Array of event objects to deduplicate.
 * @param {object}  [opts]
 * @param {boolean} [opts.ignoreId=false] - Dedup on title+date only (ignore id).
 * @returns {object[]} New array with duplicates removed, order preserved.
 */
export const dedupEvents = (evts, { ignoreId = false } = {}) => {
  const seen = new Set();
  const out = [];
  for (const e of evts) {
    const idKey = !ignoreId && e.id ? `id:${e.id}` : null;
    // Content key via occurrenceKeyOf, NOT a local `title.toLowerCase().trim()`.
    // The loose fold admitted one occurrence twice when two runs spelled it
    // differently — a single row held both
    // `niño 3.4 index reaches +2.7°c::2026-08-12` and
    // `nino 3.4 index reaches +2.7c::2026-08-12`. occurrenceKeyOf folds NFKD,
    // combining marks and decorative characters, so those converge.
    const titleKey = occurrenceKeyOf(e);
    if (idKey && seen.has(idKey)) continue;
    if (titleKey && seen.has(titleKey)) continue;
    if (idKey) seen.add(idKey);
    if (titleKey) seen.add(titleKey);
    out.push(e);
  }
  return out;
};

/**
 * Rebuilds the event timeline on reforecast, ACCUMULATING past triggers while
 * refreshing future ones.
 *
 * A reforecast is a FULL re-run of the forecast (the same server pipeline as a
 * brand-new forecast), so `newEvents` is the complete freshly-predicted timeline.
 * But the user wants the PAST to accumulate over time rather than be regenerated
 * on every run: as more time passes, more triggers move behind the today line
 * and the history of what has occurred should build up. So the two halves of the
 * timeline are treated differently, split at `nowDate` by each event's effective
 * end date (`date_end` if present, else `date`):
 *
 * - **Future (end > nowDate) — REFRESHED.** Only the fresh run's future events
 *   survive; the prior vintage's future predictions are discarded (superseded by
 *   the new estimates).
 * - **Past (end ≤ nowDate) — ACCUMULATED (added, not refreshed).** Every past
 *   event from the prior vintage (`oldEvents`) is kept AND any past events the
 *   fresh run surfaces (newly-occurred triggers) are added. Old-past events are
 *   ordered FIRST so they WIN the dedup over a same-id/title new-past event — i.e.
 *   an already-established past trigger is preserved as-is ("add, don't refresh"),
 *   while a genuinely new occurrence the prior run never had gets appended (this
 *   is the fix for the old behavior, which dropped the server's new-past events so
 *   newly-occurred triggers never surfaced).
 *
 * Finally, every merged event is **re-stamped `isPast`** off `nowDate` (past → true,
 * future → false). This is load-bearing, not cosmetic: the timeline renders a pie
 * for `isPast === false` and a grey ✓ for `isPast === true`, and it trusts the
 * STORED flag (no live recompute). An accumulated old event carries a stale
 * `isPast:false` from a vintage when its date was still in the future, so without
 * this re-stamp it would draw as a **pending pie behind the today line**. The
 * re-stamp guarantees the invariant: nothing left of today is ever a pie / pending
 * trigger — only occurred (✓) events. Re-stamping clones (never mutates the prior
 * run's stored event objects).
 *
 * Dedup keys on CONTENT (title+date), not the event `id` (`ignoreId`): the
 * forecast server hands out positional ids (`e1`, `e2`, … reset every run), so an
 * old past event and a genuinely different new past event routinely collide on id.
 * Keying on id would drop the new one and the past could never grow — the exact
 * bug this accumulation logic must avoid. Two events are "the same occurrence"
 * iff same title + same date.
 *
 * The branch fork event (`preserveEvent`), when supplied, is prepended first and
 * always kept — it wins any dedup collision with a same title+date event, is
 * excluded from the old-past bucket so it is never double-added, and is left
 * exactly as passed (NOT re-stamped — its ✓/✕ fork marker renders specially).
 *
 * `nowDate` defaults to real wall-clock now when absent (call sites pass a
 * mock-today-aware instant; the default keeps the fn usable standalone). `genDate`
 * is accepted for call-site compatibility but unused.
 *
 * After deduplication the result is sorted ascending by `date`.
 *
 * @param {object}   opts
 * @param {object[]} [opts.oldEvents]     - The prior vintage's events (the past
 *                                          half is accumulated from here).
 * @param {object[]} opts.newEvents       - Events from the fresh reforecast
 *                                          response (full timeline, isPast-stamped).
 * @param {Date}     [opts.nowDate]       - The "today" boundary (mock-today aware).
 * @param {object}  [opts.preserveEvent]  - Optional event always kept as-is
 *                                          (branch fork event).
 * @returns {import("./model.js").ForecastEvent[]} Merged, deduplicated,
 *   date-sorted, isPast-restamped, occurrenceKey-stamped event array. Every
 *   event's `id` is preserved verbatim from the run that minted it. Typed as
 *   ForecastEvent[] (not the looser object[]) so strict-TS call sites can assign
 *   it straight into a run's events payload.
 */
export const mergeReforecastEvents = ({ oldEvents, newEvents, nowDate, preserveEvent }) => {
  // Note: no `= []` parameter defaults — guard the arrays inside instead. A
  // param with an `[]` default infers as `never[]`, which narrows this fn's
  // return to `object[]` and breaks the strict-TS call sites that assign the
  // result into a `ForecastEvent[]` payload; implicit-any params keep it `any[]`.
  const olds = Array.isArray(oldEvents) ? oldEvents : [];
  const news = Array.isArray(newEvents) ? newEvents : [];
  // Boundary between accumulated-past and refreshed-future. An event has "occurred"
  // (is past) once its effective end (date_end || date) is at or before nowDate —
  // the same `<=` isPast semantics as filterAndStampEvents (lib/forecast/model.ts).
  const boundary = nowDate instanceof Date && !isNaN(nowDate.getTime()) ? nowDate : new Date();
  const occurredBy = (e) => {
    const end = endDateOf(e);
    return end != null && end <= boundary;
  };

  // Accumulated past: prior-vintage past events (old wins dedup → not refreshed),
  // excluding the fork event which is prepended separately, then the fresh run's
  // past events (newly-occurred triggers get added). Refreshed future: fresh only.
  // Exclude the fork event (it's prepended separately) by id — but ONLY when it
  // actually has an id. A hydrated/legacy forkEvent can omit `id`, and an
  // unguarded `e.id === preserveEvent.id` would be `undefined === undefined` for
  // every id-less old past event, silently dropping them. (The prepended fork
  // event still wins the content dedup against any true duplicate below.)
  const oldPast = olds.filter(
    (e) =>
      occurredBy(e) && !(preserveEvent && preserveEvent.id != null && e.id === preserveEvent.id),
  );
  const newPast = news.filter(occurredBy);
  const newFuture = news.filter((e) => !occurredBy(e));

  // Fork event first (wins dedup), then accumulated past (old-first so old wins),
  // then refreshed future. Dedup is first-wins.
  const candidates = [preserveEvent, ...oldPast, ...newPast, ...newFuture].filter(Boolean);
  // Sort ascending by date. `preserveEvent` (the fork event) is allowed to have
  // no date; map missing/invalid dates to -Infinity so they sort deterministically
  // first (keeping the prepended fork event at the front) rather than relying on
  // how the engine treats a NaN comparator. Guard null/undefined/empty EXPLICITLY
  // before constructing a Date — `new Date(null)` is epoch 0 (not NaN) and would
  // otherwise sort a null-dated event at 1970 instead of first.
  const t = (e) => {
    const d = e && e.date;
    if (d == null || d === "") return -Infinity;
    const ms = new Date(d).getTime();
    return Number.isNaN(ms) ? -Infinity : ms;
  };
  // ignoreId: the server recycles positional event ids every run, so dedup on
  // content (title+date) — keying on id would drop genuinely-new past events that
  // happen to reuse an old id, and the accumulated past could never grow.
  const merged = dedupEvents(candidates, { ignoreId: true }).sort((a, b) => t(a) - t(b));

  // Finalize each merged event with (a) a re-stamped `isPast` off nowDate so
  // nothing LEFT of the today line renders as a pending pie (see the docstring),
  // and (b) a stamped `occurrenceKey`.
  //
  // `id` IS NEVER REWRITTEN HERE. It is the name the run that minted this event
  // gave it (`e1`, `h3`), and it is the join key back to that run's artifacts —
  // the SSE frames, the envelope, and the report payload the derived-branch
  // handler reads. Rewriting it to a content id is what made a persisted row say
  // `ev:broad intense flu activity in early february::2027-02-13` while the report
  // behind the same pointer said `e6`, so the server's lookup missed and 409'd.
  //
  // The merged array may therefore contain DUPLICATE ids — an accumulated past
  // event from an older vintage and a fresh event can both be `e3`, because every
  // run restarts at `e1`. That is expected and harmless now: nothing keys on `id`
  // for uniqueness. `occurrenceKey` is the unique, cross-vintage handle, and it is
  // what React nodes, the slide-anchor cache, collapse/promote sets and
  // live→done reconciliation use.
  //
  // This is also why the old `keepFreshIds` opt-in is gone. It existed only
  // because one field did two jobs, and it could only ever be opt-in because a
  // preserved run id re-addressed whatever the slide-anchor cache still held under
  // `<owner>::e1` from an earlier vintage. With anchors keyed on `occurrenceKey`
  // that hazard does not exist, so the typed-reforecast and pickup paths converge
  // and cannot drift apart again.
  //
  // The fork event (preserveEvent) is returned untouched — branch fork-matching and
  // triggerEvId reference its client-minted id, and it is not re-stamped.
  //
  // Clone rather than mutate — an accumulated old event is still owned by the
  // prior run's stored state.
  const finalized = merged.map((e) => {
    if (e === preserveEvent) return e;
    const end = endDateOf(e);
    const nextIsPast = end != null ? end <= boundary : e.isPast;
    // Return the SAME OBJECT when nothing about it changes. The done-time reconcile
    // compares the landed events against the live projection, and a needless clone
    // is a needless diff. An event that already carries a key and whose isPast is
    // unchanged is already correct; anything else is cloned before being stamped, so
    // the prior vintage's stored object is never mutated.
    const keyed = typeof e.occurrenceKey === "string" && e.occurrenceKey !== "";
    if (keyed && e.isPast === nextIsPast) return e;
    return { ...e, isPast: nextIsPast };
  });
  // Stamp AFTER cloning, so the key lands on the clone this merge returns rather
  // than on the prior vintage's stored object. Idempotent: a clone that already
  // carries a key (every event that came through an ingest point does) keeps it,
  // which is what holds a node's identity still across the merge.
  //
  // preserveEvent is excluded — it is returned exactly as passed, so stamping it
  // would mutate the caller's object. It needs no key: its id is client-minted
  // (`fk-<uuid>` / `ce-<…>`) and already stable across vintages, and `eventKey()`
  // falls back to `id` for precisely this case.
  stampOccurrenceKeys(finalized.filter((e) => e !== preserveEvent));
  return finalized;
};
